import { gql } from '@apollo/client';

import {
    INCOMPATIBLE_CLINICIAN_RESULT_FRAGMENT,
    INCOMPATIBLE_DATE_RESULT_FRAGMENT,
    RECOMMENDATION_DEFINITIONS_FRAGMENT,
} from './visitReassignment';

// Badge-free item fragments. The new visitAssignment surface still returns
// `earnedBadges`, but it is always `[]` in V1 — we don't select it and there is
// no badge UI in the assignment flow. `tiebreakerValue` is selected for
// forward-compatibility; it isn't wired to any sorting yet (the server returns
// recommendations pre-ranked).
export const ASSIGNMENT_CLINICIAN_RECOMMENDATION_ITEM_FRAGMENT = gql`
    fragment AssignmentClinicianRecommendationItem on ClinicianRecommendationItem {
        rank
        clinician {
            id
            fullName
            externalIds {
                value
            }
        }
        scoringFactors {
            code
            sortValue
            tiebreakerValue
            displayValue
            isApplicable
        }
    }
`;

export const ASSIGNMENT_DATE_RECOMMENDATION_ITEM_FRAGMENT = gql`
    fragment AssignmentDateRecommendationItem on DateRecommendationItem {
        rank
        date
        scoringFactors {
            code
            sortValue
            tiebreakerValue
            displayValue
            isApplicable
        }
    }
`;

// Query 1 — clinician recommendations (date held fixed).
// Note the extra nesting vs reassignment: visitAssignment is a namespace object
// and the operations are fields on it. `definitions` is flat (one
// RecommendationDefinitions per query), not grouped by recommendation type.
export const VISIT_ASSIGNMENT_CLINICIAN_RECOMMENDATIONS_QUERY = gql`
    query VisitAssignmentClinicianRecommendations($input: VisitAssignmentInput!) {
        decisionSupport {
            visitAssignment {
                clinicianRecommendations(input: $input) {
                    definitions {
                        ...RecommendationDefinitions
                    }
                    results {
                        visitId
                        clinicianRecommendation {
                            fixedDate
                            recommendations {
                                ...AssignmentClinicianRecommendationItem
                            }
                            incompatibleClinicians {
                                ...IncompatibleClinicianResult
                            }
                        }
                    }
                }
            }
        }
    }
    ${RECOMMENDATION_DEFINITIONS_FRAGMENT}
    ${ASSIGNMENT_CLINICIAN_RECOMMENDATION_ITEM_FRAGMENT}
    ${INCOMPATIBLE_CLINICIAN_RESULT_FRAGMENT}
`;

// Query 2 — date recommendations (clinicians held fixed).
export const VISIT_ASSIGNMENT_DATE_RECOMMENDATIONS_QUERY = gql`
    query VisitAssignmentDateRecommendations($input: VisitAssignmentInput!) {
        decisionSupport {
            visitAssignment {
                dateRecommendations(input: $input) {
                    definitions {
                        ...RecommendationDefinitions
                    }
                    results {
                        visitId
                        dateRecommendation {
                            fixedClinicianAppointments {
                                clinicianAppointmentId
                                clinicianId
                            }
                            recommendations {
                                ...AssignmentDateRecommendationItem
                            }
                            incompatibleDates {
                                ...IncompatibleDateResult
                            }
                        }
                    }
                }
            }
        }
    }
    ${RECOMMENDATION_DEFINITIONS_FRAGMENT}
    ${ASSIGNMENT_DATE_RECOMMENDATION_ITEM_FRAGMENT}
    ${INCOMPATIBLE_DATE_RESULT_FRAGMENT}
`;
